/**
 * Click-to-edit protocol for the marketing page preview.
 *
 * The preview is rendered in <iframe sandbox="allow-same-origin allow-scripts" srcDoc>. The page's
 * own scripts are blocked by a CSP meta injected by the server, so every listener here belongs to
 * the admin app (the parent). WebKit only dispatches parent listeners into a sandboxed document when
 * allow-scripts is set, which is why the CSP, not the sandbox, is what blocks page scripts.
 * (Verified in Chrome, WebKit and Firefox by the Phase 0a spike.)
 *
 * Units are elements marked data-tt-unit="uN" with data-tt-kind:
 *   html  element whose innerHTML is the editable value (text plus inline formatting)
 *   text  <span> wrapping one text node; textContent is the value
 *   attr  <img> whose alt text is edited in the side panel, not in place
 *
 * Dirtiness is measured against the browser's own serialisation captured at install, so the
 * entity normalisation innerHTML performs (&rsquo; -> ’) never counts as an edit.
 */

const isElement = (node) => node && node.nodeType === 1;

export function installCanvasBridge(doc, options) {
  const {
    canEdit,
    onSelect = () => {},
    onChange = () => {},
    onNavigate = () => {},
    onLinkChip = () => {},
    onSaveShortcut = () => {},
  } = options;
  // May be a function, so a permissions change after load needs no reinstall.
  const editable = typeof canEdit === 'function' ? canEdit : () => !!canEdit;

  const units = new Map();
  const original = new Map();
  doc.querySelectorAll('[data-tt-unit]').forEach((el) => {
    const id = el.getAttribute('data-tt-unit');
    units.set(id, el);
    original.set(id, readValue(el));
  });

  let active = null;
  const win = doc.defaultView;

  function kindOf(el) {
    return el.getAttribute('data-tt-kind');
  }

  function readValue(el) {
    switch (kindOf(el)) {
      case 'html':
        return el.innerHTML;
      case 'text':
        return el.textContent;
      default:
        return el.getAttribute('alt') ?? '';
    }
  }

  function currentValue(el) {
    const id = el.getAttribute('data-tt-unit');
    let value = readValue(el);
    // contentEditable turns typed spaces next to other spaces into NBSP; keep them only if the
    // original text had them, so a plain edit does not introduce &nbsp; into the source.
    if (kindOf(el) === 'text' && !original.get(id).includes(' ')) {
      value = value.replace(/ /g, ' ');
    }
    return value;
  }

  /** Reports the unit's value to the editor: null when it matches the original again. */
  function report(el) {
    const id = el.getAttribute('data-tt-unit');
    const value = currentValue(el);
    const changed = value !== original.get(id);
    if (changed) {
      el.setAttribute('data-tt-dirty', '');
    } else {
      el.removeAttribute('data-tt-dirty');
    }
    onChange(id, changed ? value : null);
  }

  function reportLink() {
    if (!active) {
      onLinkChip(null);
      return;
    }
    const link = active.closest('a[href]');
    if (!link) {
      onLinkChip(null);
      return;
    }
    const rect = link.getBoundingClientRect();
    onLinkChip({ href: link.getAttribute('href'), rect: { top: rect.top, left: rect.left, bottom: rect.bottom, width: rect.width } });
  }

  function activate(el, clickEvent) {
    active = el;
    el.setAttribute('data-tt-active', '');
    if (editable()) {
      el.setAttribute('contenteditable', 'true');
      el.setAttribute('spellcheck', 'true');
      el.focus({ preventScroll: true });
      if (clickEvent) {
        placeCaretAt(clickEvent.clientX, clickEvent.clientY, el);
      } else {
        placeCaretAtEnd(el);
      }
    }
    reportLink();
  }

  function deactivate() {
    if (!active) {
      return;
    }
    const el = active;
    active = null;
    el.removeAttribute('data-tt-active');
    el.removeAttribute('contenteditable');
    el.removeAttribute('spellcheck');
    report(el);
    onLinkChip(null);
  }

  function placeCaretAt(x, y, el) {
    let range = null;
    if (doc.caretRangeFromPoint) {
      range = doc.caretRangeFromPoint(x, y);
    } else if (doc.caretPositionFromPoint) {
      const pos = doc.caretPositionFromPoint(x, y);
      if (pos) {
        range = doc.createRange();
        range.setStart(pos.offsetNode, pos.offset);
        range.collapse(true);
      }
    }
    if (!range || !el.contains(range.startContainer)) {
      placeCaretAtEnd(el);
      return;
    }
    const selection = doc.getSelection();
    selection.removeAllRanges();
    selection.addRange(range);
  }

  function placeCaretAtEnd(el) {
    const selection = doc.getSelection();
    selection.selectAllChildren(el);
    selection.collapseToEnd();
  }

  // ------------------------------------------------------------------ listeners

  const onClick = (e) => {
    const target = isElement(e.target) ? e.target : e.target?.parentElement;
    if (!target) {
      return;
    }
    // Nothing in the preview may navigate the frame, submit, or toggle a <details>.
    if (target.closest('a, button, summary, label, input, select, textarea, form')) {
      e.preventDefault();
    }
    const unit = target.closest('[data-tt-unit]');
    const inChrome = target.closest('[data-tt-chrome]');
    if (unit && !inChrome) {
      const id = unit.getAttribute('data-tt-unit');
      if (kindOf(unit) === 'attr') {
        deactivate();
        onSelect(id);
        return;
      }
      if (active !== unit) {
        deactivate();
        activate(unit, e);
      }
      onSelect(id);
      reportLink();
      return;
    }
    deactivate();
    const link = target.closest('a[href]');
    if (link) {
      onNavigate(link.getAttribute('href'));
    }
  };

  const onKeyDown = (e) => {
    const mod = e.metaKey || e.ctrlKey;
    const key = e.key.toLowerCase();
    if (mod && key === 's') {
      e.preventDefault();
      deactivate();
      onSaveShortcut();
      return;
    }
    if (!active || !editable()) {
      return;
    }
    if (e.key === 'Enter') {
      e.preventDefault();
      if (kindOf(active) === 'html') {
        doc.execCommand('insertLineBreak');
      }
      return;
    }
    if (e.key === ' ' && active.closest('summary, button')) {
      // Space is the activation key for these; cancelling the toggle also swallows the space.
      e.preventDefault();
      doc.execCommand('insertText', false, ' ');
      return;
    }
    if (mod && ['b', 'i', 'u'].includes(key)) {
      e.preventDefault();
      return;
    }
    if (e.key === 'Escape') {
      e.preventDefault();
      const el = active;
      deactivate();
      el.blur();
    }
  };

  const onInput = () => {
    if (active) {
      report(active);
    }
  };

  const onPaste = (e) => {
    if (!active || !editable()) {
      return;
    }
    e.preventDefault();
    const text = (e.clipboardData?.getData('text/plain') ?? '').replace(/\s*\r?\n\s*/g, ' ');
    doc.execCommand('insertText', false, text);
  };

  const onDrop = (e) => e.preventDefault();
  const onDragStart = (e) => e.preventDefault();
  const onScroll = () => reportLink();

  doc.addEventListener('click', onClick, true);
  doc.addEventListener('keydown', onKeyDown, true);
  doc.addEventListener('input', onInput, true);
  doc.addEventListener('paste', onPaste, true);
  doc.addEventListener('drop', onDrop, true);
  doc.addEventListener('dragstart', onDragStart, true);
  win?.addEventListener('scroll', onScroll, { passive: true });

  // ------------------------------------------------------------------ API

  return {
    /** The browser's serialisation of each unit at install time. */
    original(id) {
      return original.get(id);
    },

    has(id) {
      return units.has(id);
    },

    /** Replaces a unit's content (edits from the side panel, reverts). Reports the change. */
    setUnitValue(id, value) {
      const el = units.get(id);
      if (!el) {
        return;
      }
      const next = value ?? original.get(id);
      switch (kindOf(el)) {
        case 'html':
          if (el.innerHTML !== next) el.innerHTML = next;
          break;
        case 'text':
          if (el.textContent !== next) el.textContent = next;
          break;
        default:
          el.setAttribute('alt', next);
      }
      report(el);
    },

    revert(id) {
      this.setUnitValue(id, original.get(id));
    },

    /** Scrolls a unit into view and flashes its outline. */
    highlight(id) {
      const el = units.get(id);
      if (!el) {
        return;
      }
      el.scrollIntoView({ block: 'center', behavior: 'smooth' });
      if (el !== active) {
        el.setAttribute('data-tt-active', '');
        win.setTimeout(() => {
          if (el !== active) el.removeAttribute('data-tt-active');
        }, 1400);
      }
    },

    /** Starts editing a unit as if it had been clicked (caret at the end). */
    focus(id) {
      const el = units.get(id);
      if (!el || kindOf(el) === 'attr') {
        return;
      }
      if (active !== el) {
        deactivate();
        el.scrollIntoView({ block: 'center' });
        activate(el, null);
      }
    },

    /** True when the unit takes up no space in the preview (JS-only tabs, closed menus, display:none). */
    isHidden(id) {
      const el = units.get(id);
      return !el || el.getClientRects().length === 0;
    },

    /** Commits the active unit, e.g. before saving. */
    blur() {
      deactivate();
    },

    destroy() {
      doc.removeEventListener('click', onClick, true);
      doc.removeEventListener('keydown', onKeyDown, true);
      doc.removeEventListener('input', onInput, true);
      doc.removeEventListener('paste', onPaste, true);
      doc.removeEventListener('drop', onDrop, true);
      doc.removeEventListener('dragstart', onDragStart, true);
      win?.removeEventListener('scroll', onScroll);
    },
  };
}

/**
 * Maps a link inside the site to the page path that serves it (mirror of SitePaths.fromUrlPath):
 * "/" -> site/index.html, "/features/desk-booking" -> site/features/desk-booking.html.
 * Returns null for anything that is not a root-relative page link.
 */
export function pagePathForHref(href) {
  if (!href || !href.startsWith('/') || href.startsWith('//')) {
    return null;
  }
  let path = href.split(/[?#]/)[0];
  while (path.length > 1 && path.endsWith('/')) {
    path = path.slice(0, -1);
  }
  if (path === '/') {
    return 'site/index.html';
  }
  if (path.toLowerCase().endsWith('.html')) {
    path = path.slice(0, -5);
  }
  if (path.includes('..') || path.includes('//')) {
    return null;
  }
  return `site${path}.html`;
}
